import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { getVendorDirectoryTaxonomy } from "../../api/clientVendorDirectoryApi";
import {
  activateVendorDirectoryCollectionPlan,
  createVendorDirectoryCollectionPlan,
  getVendorDirectoryCollectionPlan,
  listVendorDirectoryCollectionPlans,
  listVendorDirectoryCollectionSources,
  listVendorDirectoryConnectors,
  pauseVendorDirectoryCollectionPlan,
  updateVendorDirectoryCollectionPlan,
  previewVendorDirectoryCollectionPlan,
  startVendorDirectoryCollectionRun,
} from "../../api/clientVendorDirectoryCollectionApi";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.collectionPlans";

const STATUS_FILTERS = ["", "draft", "active", "paused", "disabled", "invalid"];

const EMPTY_DRAFT = {
  name: "",
  sourceId: "",
  connectorKey: "",
  categoryKeys: [],
  municipalityIds: [],
  searchTermsText: "",
};

function planBadgeClass(status) {
  if (status === "active") return "cvd-badge--published";
  if (["disabled", "invalid"].includes(status)) return "cvd-badge--suspended";
  if (status === "paused") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function parseSearchTerms(value) {
  return String(value || "")
    .split(/[\n,]/)
    .map((term) => term.trim())
    .filter(Boolean);
}

function draftToPayload(draft, { asDraft = false } = {}) {
  return {
    name: draft.name.trim(),
    sourceId: draft.sourceId,
    connectorKey: draft.connectorKey,
    categoryKeys: draft.categoryKeys,
    municipalityIds: draft.municipalityIds,
    searchTerms: parseSearchTerms(draft.searchTermsText),
    scheduleMode: "manual",
    ...(asDraft ? { status: "draft" } : {}),
  };
}

function planToDraft(plan) {
  const categoryKeys = (plan?.configurationSnapshot?.categories || [])
    .map((category) => category.key || category.categoryKey || category.category_key)
    .filter(Boolean);
  return {
    name: plan?.name || "",
    sourceId: plan?.sourceId || "",
    connectorKey: plan?.connectorKey || "",
    categoryKeys,
    municipalityIds: plan?.municipalityIds || [],
    searchTermsText: (plan?.searchTerms || []).join("\n"),
  };
}

function PlanStatusBadge({ status, t }) {
  return (
    <span className={`cvd-badge ${planBadgeClass(status)}`}>
      {t(`${COPY_PREFIX}.status.${status}`, {}, status)}
    </span>
  );
}

function MetricCard({ label, value, hint }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
      {hint ? <span className="cvd-metric-card__hint">{hint}</span> : null}
    </article>
  );
}

function ChipToggle({ label, selected, onClick }) {
  return (
    <button
      type="button"
      className={`cvd-plan-chip${selected ? " is-selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function filterByQuery(items, query, getLabel) {
  const normalized = String(query || "").trim().toLowerCase();
  if (!normalized) return items;
  return items.filter((item) => String(getLabel(item)).toLowerCase().includes(normalized));
}

function FormSection({ step, title, hint, children }) {
  return (
    <section className="cvd-plan-form-section">
      <div className="cvd-plan-form-section__head">
        <span className="cvd-plan-form-section__step" aria-hidden="true">
          {step}
        </span>
        <div>
          <h3 className="cvd-plan-form-section__title">{title}</h3>
          {hint ? <p className="cvd-plan-form-section__hint muted">{hint}</p> : null}
        </div>
      </div>
      <div className="cvd-plan-form-section__body">{children}</div>
    </section>
  );
}

function SourceCard({ source, selected, onSelect, t }) {
  return (
    <button
      type="button"
      className={`cvd-plan-source-card${selected ? " is-selected" : ""}`}
      aria-pressed={selected}
      onClick={() => onSelect(source.id)}
    >
      <strong className="cvd-plan-source-card__name">{source.name}</strong>
      <span className="cvd-plan-source-card__meta">
        {t(`${COPY_PREFIX}.form.connector`)} · {source.connectorKey}
      </span>
      {source.legalUseStatus ? (
        <span className="cvd-plan-source-card__badge">{source.legalUseStatus}</span>
      ) : null}
    </button>
  );
}

function FormChecklist({ items }) {
  return (
    <ul className="cvd-plan-checklist">
      {items.map((item) => (
        <li key={item.label} className={item.done ? "is-done" : ""}>
          <span className="cvd-plan-checklist__mark" aria-hidden="true">
            {item.done ? "✓" : "○"}
          </span>
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

function ParsedTermsPreview({ terms }) {
  if (!terms.length) return null;
  return (
    <div className="cvd-plan-terms-preview" aria-live="polite">
      {terms.map((term) => (
        <span key={term} className="cvd-plan-term-tag">
          {term}
        </span>
      ))}
    </div>
  );
}

function CreateCollectionPlanForm({
  mode = "create",
  draft,
  sources,
  connectors,
  categories,
  municipalities,
  selectedSource,
  preview,
  previewLoading,
  previewError,
  canPreview,
  canSave,
  actionPending,
  t,
  onUpdateDraft,
  onSelectSource,
  onToggleCategory,
  onToggleMunicipality,
  onClearScope,
  onPreview,
  onSave,
  onCancel,
}) {
  const [scopeFilter, setScopeFilter] = useState("");

  const parsedTerms = useMemo(() => parseSearchTerms(draft.searchTermsText), [draft.searchTermsText]);
  const filteredCategories = useMemo(
    () => filterByQuery(categories, scopeFilter, (category) => category.name || category.key),
    [categories, scopeFilter]
  );
  const filteredMunicipalities = useMemo(
    () => filterByQuery(municipalities, scopeFilter, (municipality) => municipality.name),
    [municipalities, scopeFilter]
  );

  const scopeCount = draft.categoryKeys.length + draft.municipalityIds.length + parsedTerms.length;
  const hasScope = scopeCount > 0 || selectedSource?.connectorKey === "fixture_csv_file";
  const connectorChoices = useMemo(() => {
    const available = connectors || [];
    const compatible = available.filter((connector) => {
      if (!selectedSource?.sourceType) return true;
      return (
        (connector.sourceTypes || []).includes(selectedSource.sourceType) ||
        connector.connectorKey === draft.connectorKey
      );
    });
    const list = compatible.length ? compatible : available;
    if (draft.connectorKey && !list.some((connector) => connector.connectorKey === draft.connectorKey)) {
      return [{ connectorKey: draft.connectorKey }, ...list];
    }
    return list;
  }, [connectors, draft.connectorKey, selectedSource]);
  const isEdit = mode === "edit";

  const checklist = [
    { label: t(`${COPY_PREFIX}.form.checkName`), done: Boolean(draft.name.trim()) },
    { label: t(`${COPY_PREFIX}.form.checkSource`), done: Boolean(draft.sourceId) },
    { label: t(`${COPY_PREFIX}.form.checkScope`), done: hasScope },
  ];

  return (
    <section className="cvd-panel cvd-plan-create">
      <header className="cvd-plan-create__header">
        <div>
          <h2>{t(`${COPY_PREFIX}.${isEdit ? "editTitle" : "createTitle"}`)}</h2>
          <p className="muted">{t(`${COPY_PREFIX}.${isEdit ? "editHint" : "createHint"}`)}</p>
        </div>
        <button type="button" className="cvd-btn cvd-btn--ghost" onClick={onCancel}>
          {t(`${COPY_PREFIX}.form.cancel`)}
        </button>
      </header>

      <div className="cvd-plan-create__grid">
        <div className="cvd-plan-create__form">
          <FormSection
            step="1"
            title={t(`${COPY_PREFIX}.form.basicsTitle`)}
            hint={t(`${COPY_PREFIX}.form.basicsHint`)}
          >
            <label className="cvd-field" htmlFor="plan-name">
              {t(`${COPY_PREFIX}.form.name`)}
              <input
                id="plan-name"
                value={draft.name}
                onChange={(event) => onUpdateDraft("name", event.target.value)}
                placeholder={t(`${COPY_PREFIX}.form.namePlaceholder`)}
                autoComplete="off"
              />
            </label>

            <div className="cvd-field">
              <span>{t(`${COPY_PREFIX}.form.source`)}</span>
              {sources.length === 0 ? (
                <p className="cvd-plan-create__empty muted">{t(`${COPY_PREFIX}.form.noSources`)}</p>
              ) : (
                <div className="cvd-plan-source-grid" role="radiogroup" aria-label={t(`${COPY_PREFIX}.form.source`)}>
                  {sources.map((source) => (
                    <SourceCard
                      key={source.id}
                      source={source}
                      selected={draft.sourceId === source.id}
                      onSelect={onSelectSource}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </div>

            <label className="cvd-field" htmlFor="plan-connector">
              {t(`${COPY_PREFIX}.form.connector`)}
              <select
                id="plan-connector"
                value={draft.connectorKey}
                onChange={(event) => onUpdateDraft("connectorKey", event.target.value)}
              >
                <option value="">{t(`${COPY_PREFIX}.form.connectorPlaceholder`)}</option>
                {connectorChoices.map((connector) => (
                  <option key={connector.connectorKey} value={connector.connectorKey}>
                    {connector.connectorKey}
                  </option>
                ))}
              </select>
              <span className="cvd-field__optional muted">{t(`${COPY_PREFIX}.form.connectorHint`)}</span>
            </label>
          </FormSection>

          <FormSection
            step="2"
            title={t(`${COPY_PREFIX}.form.scopeTitle`)}
            hint={t(`${COPY_PREFIX}.form.scopeHint`)}
          >
            <div className="cvd-plan-scope-toolbar">
              <label className="cvd-field cvd-plan-scope-filter" htmlFor="plan-scope-filter">
                <span className="sr-only">{t(`${COPY_PREFIX}.form.filterScope`)}</span>
                <input
                  id="plan-scope-filter"
                  type="search"
                  value={scopeFilter}
                  onChange={(event) => setScopeFilter(event.target.value)}
                  placeholder={t(`${COPY_PREFIX}.form.filterScope`)}
                />
              </label>
              {scopeCount > 0 ? (
                <div className="cvd-plan-scope-summary">
                  <span className="muted">
                    {draft.categoryKeys.length + draft.municipalityIds.length > 0
                      ? t(`${COPY_PREFIX}.form.selectedCount`, {
                          count: draft.categoryKeys.length + draft.municipalityIds.length,
                        })
                      : null}
                    {draft.categoryKeys.length + draft.municipalityIds.length > 0 && parsedTerms.length > 0
                      ? " · "
                      : null}
                    {parsedTerms.length > 0
                      ? t(`${COPY_PREFIX}.form.termsCount`, { count: parsedTerms.length })
                      : null}
                  </span>
                  <button type="button" className="cvd-btn cvd-btn--ghost cvd-btn--compact" onClick={onClearScope}>
                    {t(`${COPY_PREFIX}.form.clearScope`)}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="cvd-field">
              <span>
                {t(`${COPY_PREFIX}.form.categories`)}{" "}
                <span className="cvd-field__optional muted">({t(`${COPY_PREFIX}.form.optional`)})</span>
              </span>
              <div className="cvd-plan-chip-grid" role="group">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => (
                    <ChipToggle
                      key={category.key}
                      label={category.name || category.key}
                      selected={draft.categoryKeys.includes(category.key)}
                      onClick={() => onToggleCategory(category.key)}
                    />
                  ))
                ) : (
                  <p className="muted cvd-plan-create__empty">{t(`${COPY_PREFIX}.form.filterScope`)}</p>
                )}
              </div>
            </div>

            <div className="cvd-field">
              <span>
                {t(`${COPY_PREFIX}.form.municipalities`)}{" "}
                <span className="cvd-field__optional muted">({t(`${COPY_PREFIX}.form.optional`)})</span>
              </span>
              <div className="cvd-plan-chip-grid" role="group">
                {filteredMunicipalities.length > 0 ? (
                  filteredMunicipalities.map((municipality) => (
                    <ChipToggle
                      key={municipality.id}
                      label={municipality.name}
                      selected={draft.municipalityIds.includes(municipality.id)}
                      onClick={() => onToggleMunicipality(municipality.id)}
                    />
                  ))
                ) : (
                  <p className="muted cvd-plan-create__empty">{t(`${COPY_PREFIX}.form.filterScope`)}</p>
                )}
              </div>
            </div>

            <label className="cvd-field" htmlFor="plan-terms">
              {t(`${COPY_PREFIX}.form.searchTerms`)}{" "}
              <span className="cvd-field__optional muted">({t(`${COPY_PREFIX}.form.optional`)})</span>
              <textarea
                id="plan-terms"
                rows={4}
                value={draft.searchTermsText}
                onChange={(event) => onUpdateDraft("searchTermsText", event.target.value)}
                placeholder={t(`${COPY_PREFIX}.form.searchTermsPlaceholder`)}
              />
            </label>
            <ParsedTermsPreview terms={parsedTerms} />
          </FormSection>
        </div>

        <aside className="cvd-plan-create__sidebar">
          <div className="cvd-plan-create__sidebar-inner">
            <div className="cvd-plan-create__checklist">
              <h3>{t(`${COPY_PREFIX}.form.checklistTitle`)}</h3>
              <FormChecklist items={checklist} />
              {draft.sourceId && !hasScope ? (
                <p className="cvd-plan-create__scope-hint muted">{t(`${COPY_PREFIX}.form.scopeRequiredHint`)}</p>
              ) : null}
            </div>

            <div className="cvd-plan-create__preview">
              <h3>{t(`${COPY_PREFIX}.previewTitle`)}</h3>
              <PlanPreviewPanel preview={preview} loading={previewLoading} errorMessage={previewError} t={t} />
            </div>
          </div>

          <footer className="cvd-plan-create__footer">
            <button
              type="button"
              className="cvd-btn cvd-btn--secondary"
              disabled={!canPreview || previewLoading || actionPending}
              onClick={onPreview}
            >
              {t(`${COPY_PREFIX}.previewAction`)}
            </button>
            <button
              type="button"
              className="cvd-btn cvd-btn--primary"
              disabled={!canSave || actionPending}
              onClick={onSave}
            >
              {t(`${COPY_PREFIX}.${isEdit ? "saveChanges" : "saveDraft"}`)}
            </button>
          </footer>
        </aside>
      </div>
    </section>
  );
}

function PlanPreviewPanel({ preview, loading, errorMessage, t }) {
  if (loading) return <ClientPortalLoading message={t(`${COPY_PREFIX}.previewLoading`)} />;
  if (errorMessage) {
    return (
      <div className="cvd-alert cvd-alert--warning" role="alert">
        {errorMessage}
      </div>
    );
  }
  if (!preview) {
    return <p className="muted">{t(`${COPY_PREFIX}.previewEmpty`)}</p>;
  }

  const queries = preview.generatedQueries || [];

  return (
    <div className="cvd-plan-preview">
      <div className="cvd-metric-grid cvd-metric-grid--compact">
        <MetricCard label={t(`${COPY_PREFIX}.preview.estimatedRequests`)} value={preview.estimatedRequests} />
        <MetricCard label={t(`${COPY_PREFIX}.preview.maxPages`)} value={preview.maxPages} />
        <MetricCard label={t(`${COPY_PREFIX}.preview.maxRecords`)} value={preview.maxRecords} />
        <MetricCard label={t(`${COPY_PREFIX}.preview.queryCount`)} value={queries.length} />
      </div>

      {preview.warnings?.length ? (
        <div className="cvd-alert cvd-alert--warning" role="alert">
          {preview.warnings.join(" ")}
        </div>
      ) : null}

      {queries.length > 0 ? (
        <div className="cvd-plan-preview__queries">
          <h3>{t(`${COPY_PREFIX}.preview.generatedQueries`)}</h3>
          <ul>
            {queries.slice(0, 12).map((query) => (
              <li key={`${query.term}-${query.categoryKey || ""}-${query.municipalityKey || ""}`}>
                {query.term}
              </li>
            ))}
          </ul>
          {queries.length > 12 ? (
            <p className="muted">{t(`${COPY_PREFIX}.preview.moreQueries`, { count: queries.length - 12 })}</p>
          ) : null}
        </div>
      ) : (
        <p className="muted">{t(`${COPY_PREFIX}.preview.noQueries`)}</p>
      )}
    </div>
  );
}

function translateKey(t, key, fallback) {
  const value = t(key);
  return value !== key ? value : fallback;
}

function planKind(plan) {
  const connector = plan?.connectorKey || "";
  const sourceType = plan?.sourceType || "";
  if (
    connector === "manual_research_adapter" ||
    ["internal_research", "manual_research", "manual_entry"].includes(sourceType)
  ) {
    return "research";
  }
  if (connector === "fixture_csv_file" || sourceType === "csv_import") {
    return "csv";
  }
  return "search";
}

function collectQueries(plan) {
  const snapshot = plan?.configurationSnapshot || {};
  const generated = snapshot.generatedQueries || [];
  if (generated.length) return generated;
  return snapshot.connectorPlan?.queries || [];
}

function ChipList({ items, empty }) {
  if (!items.length) {
    return empty ? <p className="muted">{empty}</p> : null;
  }
  return (
    <ul className="cvd-plan-detail__chips">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function PlanDetail({ plan, basePath, runsPath, t, locale, onEdit, onActivate, onPause, onRun, actionPending }) {
  const snapshot = plan.configurationSnapshot || {};
  const kind = planKind(plan);
  const queries = collectQueries(plan);
  const categories = (snapshot.categories || []).map((item) => item.name || item.key).filter(Boolean);
  const municipalities = (snapshot.municipalities || []).map((item) => item.name).filter(Boolean);
  const searchTerms = plan.searchTerms || [];
  const hasSearchScope = categories.length + municipalities.length + searchTerms.length > 0;
  const needsScope = kind === "search" && !hasSearchScope;
  const pages = plan.maximumPages ?? snapshot.maxPages;
  const records = plan.maximumRecords ?? snapshot.maxRecords;
  const connectorLabel = translateKey(
    t,
    `${COPY_PREFIX}.detail.connectors.${plan.connectorKey}`,
    plan.connectorKey
  );
  const legalLabel = translateKey(
    t,
    `realEstate.casaMapa.admin.vendorDirectoryWorkspace.sourcesPage.legalUse.${plan.legalUseStatus}`,
    plan.legalUseStatus
  );
  const retentionLabel = translateKey(
    t,
    `${COPY_PREFIX}.detail.retentionValues.${snapshot.rawResponseRetention}`,
    snapshot.rawResponseRetention
  );
  const introKey = needsScope ? "draftEmpty" : plan.status;
  const recommended =
    needsScope ? "editScope" : plan.status === "active" ? "run" : plan.status === "paused" ? "activate" : "activate";

  return (
    <div className="cvd-plan-detail">
      <Link className="cvd-plan-detail__back" to={basePath}>
        ← {t(`${COPY_PREFIX}.detail.backToList`)}
      </Link>

      <header className="cvd-plan-detail__hero">
        <div className="cvd-plan-detail__hero-copy">
          <p className="cvd-plan-detail__eyebrow">{t(`${COPY_PREFIX}.detail.eyebrow`)}</p>
          <h1>{plan.name}</h1>
          <div className="cvd-plan-detail__meta">
            <PlanStatusBadge status={plan.status} t={t} />
            <span className="muted">{formatDateTime(plan.updatedAt || plan.createdAt, locale)}</span>
          </div>
          <p>{translateKey(t, `${COPY_PREFIX}.detail.intro.${introKey}`, t(`${COPY_PREFIX}.detail.intro.draft`))}</p>
          <p className="cvd-plan-detail__kind">{t(`${COPY_PREFIX}.detail.kindIntro.${kind}`)}</p>
        </div>
        <div className="cvd-plan-detail__hero-actions">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_EXECUTE}>
            {plan.status !== "disabled" && plan.status !== "invalid" ? (
              <button
                type="button"
                className={`cvd-btn ${recommended === "run" ? "cvd-btn--primary" : "cvd-btn--secondary"}`}
                disabled={actionPending}
                onClick={() => onRun(plan.id)}
              >
                {t(`${COPY_PREFIX}.runNow`)}
              </button>
            ) : null}
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_CONFIGURE}>
            {plan.status !== "active" ? (
              <button
                type="button"
                className={`cvd-btn ${recommended === "activate" ? "cvd-btn--primary" : "cvd-btn--secondary"}`}
                disabled={actionPending}
                onClick={() => onActivate(plan.id)}
              >
                {t(`${COPY_PREFIX}.activate`)}
              </button>
            ) : (
              <button
                type="button"
                className="cvd-btn cvd-btn--secondary"
                disabled={actionPending}
                onClick={() => onPause(plan.id)}
              >
                {t(`${COPY_PREFIX}.pause`)}
              </button>
            )}
            <button
              type="button"
              className={`cvd-btn ${recommended === "editScope" ? "cvd-btn--primary" : "cvd-btn--ghost"}`}
              disabled={actionPending}
              onClick={onEdit}
            >
              {t(`${COPY_PREFIX}.edit`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>
      </header>

      <section className="cvd-plan-detail__guide" aria-labelledby="cvd-plan-guide-title">
        <h2 id="cvd-plan-guide-title">{t(`${COPY_PREFIX}.detail.actionsTitle`)}</h2>
        <p className="cvd-plan-detail__recommended">
          {t(`${COPY_PREFIX}.detail.next.${recommended}`)}
        </p>
        <ul>
          {needsScope ? <li>{t(`${COPY_PREFIX}.detail.actions.editHint`)}</li> : null}
          {plan.status !== "active" ? <li>{t(`${COPY_PREFIX}.detail.actions.activateHint`)}</li> : (
            <li>{t(`${COPY_PREFIX}.detail.actions.pauseHint`)}</li>
          )}
          <li>{t(`${COPY_PREFIX}.detail.actions.runHint`)}</li>
          <li>
            <Link to={runsPath}>{t(`${COPY_PREFIX}.detail.actions.runs`)}</Link>
            {" — "}
            {t(`${COPY_PREFIX}.detail.actions.runsHint`)}
          </li>
        </ul>
      </section>

      <dl className="cvd-plan-detail__stats">
        <div>
          <dt>{t(`${COPY_PREFIX}.detail.stats.lastRun`)}</dt>
          <dd>{plan.lastRunAt ? formatDateTime(plan.lastRunAt, locale) : t(`${COPY_PREFIX}.detail.stats.neverRun`)}</dd>
        </div>
        <div>
          <dt>{t(`${COPY_PREFIX}.detail.stats.schedule`)}</dt>
          <dd>
            {plan.scheduleMode === "scheduled"
              ? t(`${COPY_PREFIX}.detail.stats.scheduleScheduled`)
              : t(`${COPY_PREFIX}.detail.stats.scheduleManual`)}
          </dd>
        </div>
        {kind === "search" ? (
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.stats.queries`)}</dt>
            <dd>{queries.length}</dd>
          </div>
        ) : (
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.stats.legal`)}</dt>
            <dd>{legalLabel || "—"}</dd>
          </div>
        )}
      </dl>

      <div className="cvd-plan-detail__grid">
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.collectsTitle`)}</h2>
          <dl className="cvd-dl">
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.source`)}</dt>
              <dd>{plan.sourceName || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.collector`)}</dt>
              <dd>{connectorLabel}</dd>
            </div>
          </dl>
          {kind === "research" ? (
            <p className="cvd-plan-detail__note">{t(`${COPY_PREFIX}.detail.collectsResearch`)}</p>
          ) : null}
          {kind === "csv" ? <p className="cvd-plan-detail__note">{t(`${COPY_PREFIX}.detail.collectsCsv`)}</p> : null}
          {kind === "search" ? (
            <>
              <div className="cvd-plan-detail__scope-block">
                <h3>{t(`${COPY_PREFIX}.form.categories`)}</h3>
                <ChipList items={categories} empty={t(`${COPY_PREFIX}.detail.noneSelected`)} />
              </div>
              <div className="cvd-plan-detail__scope-block">
                <h3>{t(`${COPY_PREFIX}.form.municipalities`)}</h3>
                <ChipList items={municipalities} empty={t(`${COPY_PREFIX}.detail.noneSelected`)} />
              </div>
              <div className="cvd-plan-detail__scope-block">
                <h3>{t(`${COPY_PREFIX}.form.searchTerms`)}</h3>
                <ChipList items={searchTerms} empty={t(`${COPY_PREFIX}.detail.noneSelected`)} />
              </div>
              {needsScope ? (
                <div className="cvd-plan-detail__empty-scope">
                  <p>{t(`${COPY_PREFIX}.detail.collectsEmpty`)}</p>
                  <button type="button" className="cvd-btn cvd-btn--primary" onClick={onEdit}>
                    {t(`${COPY_PREFIX}.detail.collectsEmptyAction`)}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>

        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.howTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.detail.scheduleManualHint`)}</p>
          <p>
            {t(`${COPY_PREFIX}.detail.limitsHint`, {
              pages: pages ?? "—",
              records: records ?? "—",
            })}
          </p>
          {retentionLabel ? (
            <p>{t(`${COPY_PREFIX}.detail.retentionHint`, { retention: retentionLabel })}</p>
          ) : null}
          <div className="cvd-plan-detail__panel-actions">
            <Link className="cvd-btn cvd-btn--secondary" to={runsPath}>
              {t(`${COPY_PREFIX}.viewRuns`)}
            </Link>
          </div>
        </section>
      </div>

      {kind === "search" ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.queriesTitle`)}</h2>
          {queries.length ? (
            <ChipList items={queries.map((query) => query.term).filter(Boolean)} />
          ) : (
            <p className="muted">{t(`${COPY_PREFIX}.detail.queriesEmpty`)}</p>
          )}
        </section>
      ) : null}
    </div>
  );
}

export default function ClientVendorDirectoryCollectionPlanListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const [plans, setPlans] = useState([]);
  const [sources, setSources] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerTone, setBannerTone] = useState("success");
  const [actionPending, setActionPending] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState("");
  const [connectors, setConnectors] = useState([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const statusFilter = searchParams.get("status") || "";
  const planId = searchParams.get("plan") || "";
  const basePath = `${serviciosBase}/colecciones/planes`;
  const runsPath = `${serviciosBase}/colecciones/ejecuciones`;

  const loadPlans = useCallback(async () => {
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryCollectionPlans(clientViewId, {
        status: statusFilter || undefined,
      });
      setPlans(body.plans || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setPlans([]);
    }
  }, [clientViewId, statusFilter, t]);

  const loadSources = useCallback(async () => {
    try {
      const body = await listVendorDirectoryCollectionSources(clientViewId);
      setSources((body.sources || []).filter((source) => source.collectionEnabled !== false));
    } catch {
      setSources([]);
    }
  }, [clientViewId]);

  const loadConnectors = useCallback(async () => {
    try {
      const body = await listVendorDirectoryConnectors(clientViewId);
      setConnectors(body.connectors || []);
    } catch {
      setConnectors([]);
    }
  }, [clientViewId]);

  const loadTaxonomy = useCallback(async () => {
    try {
      const body = await getVendorDirectoryTaxonomy(clientViewId);
      setTaxonomy(body.taxonomy || body);
    } catch {
      setTaxonomy(null);
    }
  }, [clientViewId]);

  const loadPlanDetail = useCallback(async () => {
    if (!planId) {
      setSelectedPlan(null);
      return;
    }
    setDetailLoading(true);
    try {
      const body = await getVendorDirectoryCollectionPlan(clientViewId, planId);
      setSelectedPlan(body.plan);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setSelectedPlan(null);
    } finally {
      setDetailLoading(false);
    }
  }, [clientViewId, planId, t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([loadPlans(), loadSources(), loadTaxonomy(), loadConnectors()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConnectors, loadPlans, loadSources, loadTaxonomy]);

  useEffect(() => {
    loadPlanDetail();
  }, [loadPlanDetail]);

  const categories = useMemo(
    () => (taxonomy?.categories || []).filter((category) => category.isMvp || category.isActive !== false),
    [taxonomy]
  );
  const municipalities = taxonomy?.municipalities || [];

  const summary = useMemo(() => {
    const active = plans.filter((plan) => plan.status === "active").length;
    const draft = plans.filter((plan) => plan.status === "draft").length;
    const paused = plans.filter((plan) => plan.status === "paused").length;
    return { total: plans.length, active, draft, paused };
  }, [plans]);

  const isEditing = Boolean(planId && editingPlanId === planId);
  const formSources = useMemo(() => {
    if (!selectedPlan?.sourceId || sources.some((source) => source.id === selectedPlan.sourceId)) {
      return sources;
    }
    return [
      ...sources,
      {
        id: selectedPlan.sourceId,
        name: selectedPlan.sourceName,
        sourceName: selectedPlan.sourceName,
        sourceKey: selectedPlan.sourceKey,
        connectorKey: selectedPlan.connectorKey,
        sourceType: selectedPlan.sourceType,
        collectionEnabled: selectedPlan.collectionEnabled !== false,
      },
    ];
  }, [selectedPlan, sources]);

  const selectedSource = useMemo(
    () => formSources.find((source) => source.id === draft.sourceId) || null,
    [draft.sourceId, formSources]
  );

  const canPreview = Boolean(draft.name.trim() && draft.sourceId && draft.connectorKey);
  const hasScope =
    parseSearchTerms(draft.searchTermsText).length > 0 ||
    draft.categoryKeys.length > 0 ||
    draft.municipalityIds.length > 0 ||
    selectedSource?.connectorKey === "fixture_csv_file" ||
    draft.connectorKey === "google_places";
  const canSave = canPreview && (isEditing || hasScope);

  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("status", value);
    else next.delete("status");
    next.delete("plan");
    setSearchParams(next, { replace: true });
  };

  const openPlan = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("plan", id);
    setSearchParams(next);
  };

  const showBanner = (message, tone = "success") => {
    setBannerMessage(message);
    setBannerTone(tone);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const updateDraft = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setPreview(null);
    setPreviewError("");
  };

  const toggleCategory = (key) => {
    setDraft((prev) => {
      const nextKeys = prev.categoryKeys.includes(key)
        ? prev.categoryKeys.filter((item) => item !== key)
        : [...prev.categoryKeys, key];
      return { ...prev, categoryKeys: nextKeys };
    });
    setPreview(null);
    setPreviewError("");
  };

  const toggleMunicipality = (id) => {
    setDraft((prev) => {
      const nextIds = prev.municipalityIds.includes(id)
        ? prev.municipalityIds.filter((item) => item !== id)
        : [...prev.municipalityIds, id];
      return { ...prev, municipalityIds: nextIds };
    });
    setPreview(null);
    setPreviewError("");
  };

  const handleClearScope = () => {
    setDraft((prev) => ({
      ...prev,
      categoryKeys: [],
      municipalityIds: [],
      searchTermsText: "",
    }));
    setPreview(null);
    setPreviewError("");
  };

  const handleCancelCreate = () => {
    setShowCreatePanel(false);
    setDraft(EMPTY_DRAFT);
    setPreview(null);
    setPreviewError("");
  };

  const handleSelectSource = (sourceId) => {
    const source = formSources.find((item) => item.id === sourceId);
    setDraft((prev) => ({
      ...prev,
      sourceId,
      connectorKey: source?.connectorKey || "",
    }));
    setPreview(null);
    setPreviewError("");
  };

  const startEdit = (plan) => {
    setDraft(planToDraft(plan));
    setPreview(null);
    setPreviewError("");
    setShowCreatePanel(false);
    setEditingPlanId(plan.id);
    if (searchParams.get("plan") !== plan.id) {
      const next = new URLSearchParams(searchParams);
      next.set("plan", plan.id);
      setSearchParams(next);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlanId("");
    setDraft(EMPTY_DRAFT);
    setPreview(null);
    setPreviewError("");
  };

  const handlePreview = async () => {
    if (!canPreview) return;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const body = await previewVendorDirectoryCollectionPlan(clientViewId, draftToPayload(draft));
      setPreview(body.preview || body);
    } catch (error) {
      setPreview(null);
      setPreviewError(error.message || t(`${COPY_PREFIX}.previewFailed`));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!canSave) return;
    setActionPending(true);
    try {
      await createVendorDirectoryCollectionPlan(clientViewId, draftToPayload(draft, { asDraft: true }));
      showBanner(t(`${COPY_PREFIX}.createSuccess`));
      setShowCreatePanel(false);
      setDraft(EMPTY_DRAFT);
      setPreview(null);
      await loadPlans();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.createFailed`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  const handleUpdate = async () => {
    if (!canSave || !planId) return;
    setActionPending(true);
    try {
      await updateVendorDirectoryCollectionPlan(clientViewId, planId, draftToPayload(draft));
      showBanner(t(`${COPY_PREFIX}.updateSuccess`));
      setEditingPlanId("");
      setDraft(EMPTY_DRAFT);
      setPreview(null);
      await loadPlanDetail();
      await loadPlans();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.updateFailed`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  const handleActivate = async (id) => {
    setActionPending(true);
    try {
      await activateVendorDirectoryCollectionPlan(clientViewId, id);
      showBanner(t(`${COPY_PREFIX}.activateSuccess`));
      await loadPlans();
      if (planId === id) await loadPlanDetail();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.actionError`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  const handlePause = async (id) => {
    setActionPending(true);
    try {
      await pauseVendorDirectoryCollectionPlan(clientViewId, id);
      showBanner(t(`${COPY_PREFIX}.pauseSuccess`));
      await loadPlans();
      if (planId === id) await loadPlanDetail();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.actionError`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  const handleRun = async (id) => {
    setActionPending(true);
    try {
      await startVendorDirectoryCollectionRun(clientViewId, { planId: id });
      showBanner(t(`${COPY_PREFIX}.runQueued`));
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.runFailed`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  if (planId) {
    if (detailLoading && !selectedPlan) {
      return (
        <div className="page-content cvd-admin">
          <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />
        </div>
      );
    }
    if (selectedPlan) {
      return (
        <div className="page-content cvd-admin cvd-collection-plans">
          {bannerMessage ? (
            <div className={`cvd-alert cvd-alert--${bannerTone === "warning" ? "warning" : "success"}`}>{bannerMessage}</div>
          ) : null}
          {isEditing ? (
            <CreateCollectionPlanForm
              mode="edit"
              draft={draft}
              sources={formSources}
              connectors={connectors}
              categories={categories}
              municipalities={municipalities}
              selectedSource={selectedSource}
              preview={preview}
              previewLoading={previewLoading}
              previewError={previewError}
              canPreview={canPreview}
              canSave={canSave}
              actionPending={actionPending}
              t={t}
              onUpdateDraft={updateDraft}
              onSelectSource={handleSelectSource}
              onToggleCategory={toggleCategory}
              onToggleMunicipality={toggleMunicipality}
              onClearScope={handleClearScope}
              onPreview={handlePreview}
              onSave={handleUpdate}
              onCancel={handleCancelEdit}
            />
          ) : (
            <PlanDetail
              plan={selectedPlan}
              basePath={basePath}
              runsPath={runsPath}
              t={t}
              locale={locale}
              onEdit={() => startEdit(selectedPlan)}
              onActivate={handleActivate}
              onPause={handlePause}
              onRun={handleRun}
              actionPending={actionPending}
            />
          )}
        </div>
      );
    }
  }

  return (
    <div className="page-content cvd-admin cvd-collection-plans">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`)}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={loadPlans}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
          <Link className="cvd-btn cvd-btn--secondary" to={runsPath}>
            {t(`${COPY_PREFIX}.runsLink`)}
          </Link>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_CONFIGURE}>
            <button
              type="button"
              className="cvd-btn cvd-btn--primary"
              onClick={() => setShowCreatePanel((open) => !open)}
            >
              {t(`${COPY_PREFIX}.createPlan`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>
      </div>

      {bannerMessage ? (
        <div className={`cvd-alert cvd-alert--${bannerTone === "warning" ? "warning" : "success"}`}>{bannerMessage}</div>
      ) : null}

      {showCreatePanel ? (
        <CreateCollectionPlanForm
          draft={draft}
          sources={formSources}
          connectors={connectors}
          categories={categories}
          municipalities={municipalities}
          selectedSource={selectedSource}
          preview={preview}
          previewLoading={previewLoading}
          previewError={previewError}
          canPreview={canPreview}
          canSave={canSave}
          actionPending={actionPending}
          t={t}
          onUpdateDraft={updateDraft}
          onSelectSource={handleSelectSource}
          onToggleCategory={toggleCategory}
          onToggleMunicipality={toggleMunicipality}
          onClearScope={handleClearScope}
          onPreview={handlePreview}
          onSave={handleCreate}
          onCancel={handleCancelCreate}
        />
      ) : null}

      {!loading && plans.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.active`)} value={summary.active} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.draft`)} value={summary.draft} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.paused`)} value={summary.paused} />
        </div>
      ) : null}

      <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
        {STATUS_FILTERS.map((value) => (
          <button
            key={value || "all"}
            type="button"
            className={`cvd-run-filter-chip${statusFilter === value ? " is-active" : ""}`}
            onClick={() => setStatusFilter(value)}
          >
            {value ? t(`${COPY_PREFIX}.status.${value}`, {}, value) : t(`${COPY_PREFIX}.allStatuses`)}
          </button>
        ))}
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={loadPlans} /> : null}

      {!loading && !errorMessage && plans.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_CONFIGURE}>
            <button type="button" className="cvd-btn cvd-btn--primary" onClick={() => setShowCreatePanel(true)}>
              {t(`${COPY_PREFIX}.createPlan`)}
            </button>
          </VendorDirectoryPermissionGate>
        </section>
      ) : null}

      {!loading && !errorMessage && plans.length > 0 ? (
        <div className="cvd-run-list">
          {plans.map((plan) => {
            const snapshot = plan.configurationSnapshot || {};
            const queryCount = snapshot.generatedQueries?.length ?? 0;
            return (
              <article key={plan.id} className="cvd-run-card">
                <div className="cvd-run-card__main">
                  <div className="cvd-run-card__head">
                    <div>
                      <button type="button" className="cvd-run-card__title" onClick={() => openPlan(plan.id)}>
                        {plan.name}
                      </button>
                      <p className="cvd-run-card__sub">
                        {plan.sourceName || "—"} · {plan.connectorKey}
                      </p>
                    </div>
                    <PlanStatusBadge status={plan.status} t={t} />
                  </div>
                  <div className="cvd-run-card__stats">
                    <span>
                      <strong>{queryCount}</strong> {t(`${COPY_PREFIX}.table.queries`).toLowerCase()}
                    </span>
                    <span>
                      <strong>{snapshot.estimatedRequests ?? "—"}</strong> {t(`${COPY_PREFIX}.table.requests`).toLowerCase()}
                    </span>
                    <span>
                      {t(`${COPY_PREFIX}.table.lastRun`)}: {formatDateTime(plan.lastRunAt, locale)}
                    </span>
                  </div>
                </div>
                <div className="cvd-run-card__actions">
                  <button type="button" className="cvd-btn cvd-btn--ghost" onClick={() => openPlan(plan.id)}>
                    {t(`${COPY_PREFIX}.viewDetails`)}
                  </button>
                  <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_CONFIGURE}>
                    <button
                      type="button"
                      className="cvd-btn cvd-btn--secondary"
                      disabled={actionPending}
                      onClick={() => startEdit(plan)}
                    >
                      {t(`${COPY_PREFIX}.edit`)}
                    </button>
                    {plan.status !== "active" ? (
                      <button
                        type="button"
                        className="cvd-btn cvd-btn--secondary"
                        disabled={actionPending}
                        onClick={() => handleActivate(plan.id)}
                      >
                        {t(`${COPY_PREFIX}.activate`)}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="cvd-btn cvd-btn--secondary"
                        disabled={actionPending}
                        onClick={() => handlePause(plan.id)}
                      >
                        {t(`${COPY_PREFIX}.pause`)}
                      </button>
                    )}
                  </VendorDirectoryPermissionGate>
                  <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_EXECUTE}>
                    <button
                      type="button"
                      className="cvd-btn cvd-btn--primary"
                      disabled={actionPending}
                      onClick={() => handleRun(plan.id)}
                    >
                      {t(`${COPY_PREFIX}.runNow`)}
                    </button>
                  </VendorDirectoryPermissionGate>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
